<template>
    <div class="home">
        <carousel v-if="dto.carousel" :model="dto.carousel" class="carousel">
        </carousel>
        <main>
            <div class="statistics">
                <div class="statistics-card">
                    <div v-if="dto.freeTextAreaEnabled" v-html="dto.freeTextAreaContent" class="free-content">
                    </div>
                    <div v-else class="get-inspired">
                        <div>
                            <h2>{{ dto.getInspired.title }}</h2>
                            <small>{{ dto.getInspired.description }}</small>
                        </div>
                        <div class="get-inspired-news">
                            <ul>
                                <li v-for="item in dto.getInspired.items" class="inspired-item">
                                    <img :src="item.image.url" />
                                    <a :href="item.link?.url || '#'" class="inspired-item-text">
                                        <h5>{{ item.title }}</h5>
                                        <p>{{ item.description }}</p>
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <form @submit.prevent="onSubmit" class="register-tree-form">
                            <h2>{{ dto.translations?.['registerTree.Form.PostCodeRightTabHeader'] }}</h2>
                            <small>{{ dto.translations?.['registerTree.Form.PostCodeRightTabInfo'] }}</small>
                            <FormField v-slot="{ componentField }" name="postCode">
                                <FormItem>
                                    <FormControl>
                                        <div class="input-with-button">
                                            <Input type="text" v-bind="componentField"
                                                :placeholder="dto.translations?.['registerTree.Form.PostCodeRightTabInputPlaceholder'] ?? ''"></Input>
                                            <Button type="submit">
                                                <SendHorizonalIcon></SendHorizonalIcon>
                                            </Button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            </FormField>
                        </form>
                    </div>
                    <img :src="statistics" alt="statistics" />
                </div>
            </div>
            <div class="image-content">
                <div v-for="item in dto.newsAndStory" class="image-content-item">
                    <div class="image-content-item-text">
                        <div v-html="item.text"></div>
                        <Button as-child v-if="item.buttonLink">
                            <a :href="item.buttonLink.url">
                                {{ item.buttonLink.text }}
                            </a>
                        </Button>
                        <div v-if="item.explanationText">{{ item.explanationText }}</div>
                    </div>
                    <img :src="item.image.url" class="image-content-item-image" />
                </div>
            </div>
            <home-news v-if="dto.news" :news="dto.news"></home-news>
        </main>
    </div>
</template>
<script setup lang="ts">
import Carousel from '@/components/Carousel/Carousel.vue';
import { IHomeProps } from './Home.model';
import { Button, ButtonVariants } from '@/components/ui/button';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import * as z from 'zod';
import { SendHorizonalIcon } from 'lucide-vue-next';
import { POST_CODE_REGEX } from '@/contants/formFieldsRegex';
import { FormField, FormItem, FormMessage } from '@/components/ui/form';
import FormControl from '@/components/ui/form/FormControl.vue';
import { Input } from '@/components/ui/input';
import HomeNews from './HomeNews.vue';
import statistics from "@/assets/statistics.png";
const { dto } = defineProps<IHomeProps>()

const getVariant = (index: number): ButtonVariants["variant"] => {
    if (index % 2 === 1) { return "outline" }
    return "default"
}

const formSchema = toTypedSchema(z.object({
    postCode: z.string("Field is required").regex(POST_CODE_REGEX, "Post code is invalid"),
}))

const form = useForm({ validationSchema: formSchema })

const onSubmit = form.handleSubmit((values) => {
    window.location.href = `/register-tree/?inputValue=${values.postCode}`
})

</script>
<style lang="scss" scoped>
.simple-statistics {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 20px;
    margin-bottom: -30px;
    z-index: 2;

    .simple-statistics-title {
        font-weight: 400;
        font-size: 2.75rem;
    }

    .simple-statistic-tiles {
        padding-top: 20px;
        display: flex;
        flex-direction: column;
        gap: 20px;
        justify-content: center;

        .simple-statistic-tile {
            width: 250px;
            min-height: 300px;
            font-size: 1.75rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            height: 200px;
            background-color: var(--background);
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.34);
            padding: 24px 12px;
            z-index: 2;

            .value-title {
                img {
                    height: 62px;
                    width: auto;
                    object-fit: contain;
                }

                display: flex;
                gap: 10px;
                align-items: center;
                color: var(--primary);
                font-weight: 800;
            }
        }
    }

}


.statistics {
    background-color: var(--primary);
    min-width: 100%;
    display: flex;
    padding-bottom: 20px;
    justify-content: center;

    .statistics-card {
        margin-top: -50px;
        background-color: white;
        display: flex;
        flex-direction: column;
        align-items: center;
        z-index: 2;
        border-radius: 12px;

        >div:last-of-type {
            border-top: 1px solid var(--primary);
        }
    }

    .statistics-title {
        padding: 12px 18px;
        display: flex;
        flex-direction: column;
        gap: 20px;
        flex: 1;

        h2 {
            font-size: 1.88rem;
            text-align: center;
        }

        .statistics-tile {
            display: flex;
            gap: 15px;
            align-items: flex-start;
            justify-content: center;

            .statistics-tile-item {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 5px;
                text-align: center;

                img {
                    height: 40px;
                    width: auto;
                    object-fit: contain;
                }
            }
        }
    }

    .get-inspired,
    .free-content {
        display: flex;
        flex-direction: column;
        padding: 12px 18px;
        gap: 20px;
        flex: 1;

        :deep(iframe) {
            width: 100%;
        }
    }

    .get-inspired {
        .register-tree-form {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;
        }

        .inspired-item {
            display: flex;
            align-items: center;
            gap: 10px;

            img {
                max-height: 130px;
                width: auto;
                object-fit: contain;
                border-radius: 8px;
            }

            .inspired-item-text {
                h5 {
                    color: var(--primary);
                    font-weight: 800;
                }

                display: flex;
                flex-direction: column;
                gap: 2px
            }
        }
    }
}


.image-content {
    .image-content-item {
        display: flex;
        flex-direction: column;

        &:nth-of-type(even) {
            background-color: var(--primary);

            a {
                background-color: white;
                color: var(--primary)
            }
        }

        .image-content-item-image {
            object-fit: cover;
            min-width: 50%;
            flex: 1;
        }

        .image-content-item-text {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 20px;
            gap: 10px;
            width: 100%;
        }
    }
}

.home {
    display: flex;
    flex-direction: column;
    align-items: center;

}


.input-with-button {
    display: flex;
    gap: 5px;
}

main {
    max-width: 95%;
    width: 100%;
    border-radius: 0 0 24px 24px;
    border: 1px solid lightgray;
    border-top: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: center;
}

.carousel {
    min-width: 100%;
}

@media (min-width: 768px) {
    main {
        max-width: 80%;
    }

    .get-inspired,
    .free-content {
        :deep(iframe) {
            width: unset;
            aspect-ratio: 16 / 9;
        }
    }

    .carousel {
        min-width: unset;
        max-width: 85%;
        width: 100%;
    }


    .statistics {
        .statistics-card {
            flex-direction: row;

            >div:last-of-type {
                border-top: unset;
                border-left: 1px solid var(--primary);
            }
        }
    }

    .simple-statistics {
        .simple-statistic-tiles {
            flex-direction: row;
        }
    }

    .image-content {
        .image-content-item {
            flex-direction: row;
            max-height: 620px;

            &:nth-of-type(even) {
                flex-direction: row-reverse;
            }
        }
    }
}
</style>